<script setup lang="ts">
const { t } = useAppI18n()
const CLOSE_IMG = 'https://resources1.interface003.com/web30/2026/06/08/2af6f5ee-7ed0-4d4b-8dff-dcf5540ccb14.png'

type SurveyTraderInfo = {
  localName?: string
  shortName?: string
  defaultName?: string
  localShortName?: string
  ico?: string
  logo?: string
}

type SurveyVrInfo = {
  url?: string
  area?: string
  time?: string
  vrShowTime?: string | null
}

type SurveyVrItem = {
  country?: string
  date?: string
  vrm?: SurveyVrInfo | null
  related?: SurveyTraderInfo | null
  trader?: SurveyTraderInfo | null
  ico?: string
  logo?: string
  company?: string
  vrShowTime?: string | null
  localcompany?: string | null
  [key: string]: unknown
}

/** 组件入参，控制 VR 弹窗显隐和当前实勘数据。 */
const props = withDefaults(
  defineProps<{
    modelValue: boolean
    item?: SurveyVrItem | null
  }>(),
  {
    item: null,
  },
)

/** 向父组件同步弹窗显隐状态。 */
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const { locale } = useAppI18n()

/** 双向绑定后的弹窗显示状态。 */
const visible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
})

/** 交易商基础信息，优先取 related，其次取 trader。 */
const traderInfo = computed(() => props.item?.related || props.item?.trader || {})

/** VR 弹窗头部展示的交易商名称。 */
const traderName = computed(
  () =>
    traderInfo.value.localName ||
    traderInfo.value.shortName ||
    traderInfo.value.defaultName ||
    traderInfo.value.localShortName ||
    props.item?.company ||
    props.item?.localcompany ||
    '--',
)

/** VR 弹窗头部展示的交易商图标。 */
const traderIcon = computed(
  () => traderInfo.value.ico || traderInfo.value.logo || props.item?.ico || props.item?.logo || '',
)

/** VR 实勘所在国家/地区。 */
const surveyCountry = computed(() => props.item?.vrm?.area || props.item?.country || '--')

const surveyDate = computed(() => formatTimestamp(props.item?.vrm?.vrShowTime) || '--')

/** 在 VR 链接后追加当前语言参数，保持内容与站点语言一致。 */
const vrUrl = computed(() => {
  const baseUrl = props.item?.vrm?.url
  if (!baseUrl) {
    return ''
  }

  const separator = baseUrl.includes('?') ? '&' : '?'
  return `${baseUrl}${separator}languagecode=${locale}`
})

/** 关闭 VR 弹窗。 */
function closeDialog() {
  visible.value = false
}
</script>

<template>
  <ClientOnly>
    <ElDialog
      v-model="visible"
      width="1000px"
      align-center
      append-to-body
      :show-close="false"
      :lock-scroll="false"
      class="survey-vr-dialog"
    >
      <div class="overflow-hidden rounded-[28px] bg-white">
        <div class="flex flex-col bg-white px-[32px] pt-[32px]">
          <div class="flex items-start gap-[8px] border-b-[0.5px] border-[rgba(5,21,36,0.06)] pb-[16px]">
            <div class="min-w-0 flex-1">
              <p class="truncate text-[20px] leading-[28px] font-[600] text-[#292a2e]">{{ t('84325') }}</p>

              <div class="mt-[8px] flex items-center gap-[12px]">
                <div class="flex min-w-0 items-center gap-[4px]">
                  <CommonImage
                    v-if="traderIcon"
                    :src="traderIcon"
                    :alt="traderName"
                    class="flex size-[20px] shrink-0 rounded-[2px] object-cover"
                    loading="lazy"
                  />
                  <span class="truncate text-[14px] leading-[22px] text-[#7d818a]">
                    {{ traderName }}
                  </span>
                </div>

                <div class="flex items-center gap-[4px]">
                  <CommonImage
                    class="size-[20px] shrink-0"
                    src="https://resources1.interface003.com/web30/2026/06/11/c1e3e37f-9ec4-4e86-a4c1-7a5798d383f1.png"
                    alt="location"
                  />
                  <span class="text-[14px] leading-[22px] text-[#7d818a]">
                    {{ surveyCountry }}
                  </span>
                </div>

                <span class="ftf text-[14px] leading-[22px] text-[#7d818a]">
                  {{ surveyDate }}
                </span>
              </div>
            </div>

            <button
              type="button"
              class="flex size-[32px] shrink-0 cursor-pointer items-center justify-center hover:opacity-80"
              @click="closeDialog"
            >
              <CommonImage class="size-[32px]" :src="CLOSE_IMG" alt="close" />
            </button>
          </div>
        </div>

        <div class="bg-white px-[32px] pt-[20px] pb-[32px]">
          <div class="overflow-hidden rounded-[12px] bg-[#f5f6fa]">
            <iframe
              v-if="vrUrl"
              :src="vrUrl"
              frameborder="0"
              allowfullscreen="true"
              webkitallowfullscreen="true"
              mozallowfullscreen="true"
              class="block h-[468px] w-full"
            ></iframe>
            <div v-else class="flex h-[468px] items-center justify-center text-[14px] leading-[22px] text-[#7d818a]">
              --
            </div>
          </div>
        </div>
      </div>
    </ElDialog>
  </ClientOnly>
</template>

<style scoped lang="less">
:global(.survey-vr-dialog) {
  overflow: hidden !important;
  border-radius: 32px !important;
  background: #fff !important;
  padding: 0 !important;
}

:global(.survey-vr-dialog .el-dialog__header) {
  display: none !important;
}

:global(.survey-vr-dialog .el-dialog__body) {
  padding: 0 !important;
}
</style>
